import { createContext, useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, LockIcon } from '@itsrever/design-system'
import { useCountries } from '@/application/queries/use-countries'
import { EU_ALIAS_CODE } from '@/domain/countries'
import { REFUND_CONFIGURATION_METHODS } from '@/domain/refund'
import {
  CONDITION_CATEGORIES,
  CONDITION_FIELDS,
  Condition,
  ConditionField,
  OPERATORS,
  Operator,
  RETURN_METHODS,
  RETURN_REASONS,
  ValueSource,
  fieldSpec,
  isComplete
} from '@/domain/warehouses'
import {
  FieldGroup,
  OpenKey,
  OperatorSegment,
  Option,
  SetOpenKey,
  TagsPicker,
  ValuePicker,
  ValueSegment,
  bindOpen
} from '@/ui/components/rule-list'

const { SegmentedChip } = Base

// The reasons of the store or group on screen; without them a rule offers the fixed list.
export interface ReturnReasonChoice {
  codes: string[]
  labelOf: (code: string) => string
}

const ReturnReasonsContext = createContext<ReturnReasonChoice | undefined>(
  undefined
)

export const ReturnReasonsProvider = ReturnReasonsContext.Provider

// Labels for the values of every listed field; values a rule already holds stay pickable.
export function useValueOptions() {
  const { t } = useTranslation()
  const { countries } = useCountries()
  const reasons = useContext(ReturnReasonsContext)
  const reasonLabel = useCallback(
    (code: string) =>
      reasons?.labelOf(code) ||
      t(`select_reason.${code}`, { defaultValue: code }),
    [reasons, t]
  )
  const methodLabel = useCallback(
    (method: string) =>
      t(`returns_refunds.refunds.methods.${method}`, { defaultValue: method }),
    [t]
  )
  const lists = useMemo<Record<Exclude<ValueSource, 'free'>, Option[]>>(() => {
    const countryOptions = countries.map((country) => ({
      value: country.iso,
      label: country.name
    }))
    return {
      countries: countryOptions,
      returnCountries: [
        {
          value: EU_ALIAS_CODE,
          label: t('warehouses.conditions.european_union')
        },
        ...countryOptions
      ],
      returnMethods: RETURN_METHODS.map((method) => ({
        value: method,
        label: t(`return_methods.${method}`)
      })),
      returnReasons: (reasons?.codes ?? RETURN_REASONS).map((reason) => ({
        value: reason,
        label: reasonLabel(reason)
      })),
      refundMethods: REFUND_CONFIGURATION_METHODS.map((method) => ({
        value: method,
        label: methodLabel(method)
      }))
    }
  }, [t, countries, reasons, reasonLabel, methodLabel])
  return useCallback(
    (source: ValueSource | undefined, current: string[]): Option[] => {
      const options = source && source !== 'free' ? lists[source] : []
      const extra = current
        .filter((value) => !options.some((option) => option.value === value))
        .map((value) => ({
          value,
          label:
            source === 'returnReasons'
              ? reasonLabel(value)
              : source === 'refundMethods'
                ? methodLabel(value)
                : value
        }))
      return [...options, ...extra]
    },
    [lists, reasonLabel, methodLabel]
  )
}

function useSummary() {
  const { t, i18n } = useTranslation()
  const optionsFor = useValueOptions()
  return (condition: Condition) => {
    const { kind, values: source } = fieldSpec(condition.field)
    const [value] = condition.values
    if (!isComplete(condition)) return t('warehouses.conditions.choose')
    if (kind === 'boolean') return t('warehouses.conditions.true_value')
    if (kind === 'money')
      return new Intl.NumberFormat(i18n.language, {
        minimumFractionDigits: 2
      }).format(Number(value) / 100)
    if (kind === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(value))
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium'
      }).format(new Date(`${value}T00:00:00`))
    if (kind === 'number' || kind === 'date') return value
    const options = optionsFor(source, condition.values)
    const names = condition.values.map(
      (v) => options.find((option) => option.value === v)?.label ?? v
    )
    return names.length <= 2
      ? names.join(', ')
      : `${names.slice(0, 2).join(', ')} +${names.length - 2}`
  }
}

export const ConditionPill: React.FC<{
  id: string
  condition: Condition
  editable: boolean
  openKey: OpenKey
  setOpenKey: SetOpenKey
  onChange: (condition: Condition) => void
  onRemove: () => void
}> = ({ id, condition, editable, openKey, setOpenKey, onChange, onRemove }) => {
  const { t } = useTranslation()
  const optionsFor = useValueOptions()
  const summary = useSummary()
  const spec = fieldSpec(condition.field)
  const label = t(`warehouses.conditions.fields.${condition.field}`)
  const bind = (part: string) => bindOpen(`${id}:${part}`, openKey, setOpenKey)
  const setValues = (values: string[]) => onChange({ ...condition, values })

  return (
    <SegmentedChip.Root invalid={!isComplete(condition)}>
      <SegmentedChip.Segment emphasis="label">{label}</SegmentedChip.Segment>
      <OperatorSegment
        label={t('warehouses.conditions.operator_label', { field: label })}
        value={condition.operator}
        options={OPERATORS[spec.kind].map((operator) => ({
          value: operator,
          label: t(`warehouses.conditions.operators.${operator}`)
        }))}
        editable={editable}
        {...bind('cmp')}
        onChange={(operator) =>
          onChange({ ...condition, operator: operator as Operator })
        }
      />
      {spec.kind === 'boolean' ? (
        <SegmentedChip.Segment>{summary(condition)}</SegmentedChip.Segment>
      ) : (
        <ValueSegment
          label={t('warehouses.conditions.value_label', { field: label })}
          summary={summary(condition)}
          className="max-w-64"
          editable={editable}
          {...bind('value')}
          list={
            (spec.kind === 'list' || spec.kind === 'text') &&
            spec.values !== 'free'
              ? {
                  options: optionsFor(spec.values, condition.values),
                  value: condition.values,
                  onChange: setValues
                }
              : undefined
          }
        >
          {spec.kind === 'list' || spec.kind === 'text' ? (
            <TagsPicker
              label={label}
              values={condition.values}
              onChange={setValues}
            />
          ) : (
            <ValuePicker
              label={label}
              kind={spec.kind}
              value={condition.values[0] ?? ''}
              onChange={(value) => setValues(value === '' ? [] : [value])}
              onDone={() => setOpenKey(null)}
            />
          )}
        </ValueSegment>
      )}
      {editable ? (
        <SegmentedChip.Remove
          removeLabel={t('warehouses.conditions.remove', { field: label })}
          onClick={onRemove}
        />
      ) : null}
    </SegmentedChip.Root>
  )
}

// A rule settings-api runs but the builder can't show: kept exactly as written, never shown as code.
export const LockedPill: React.FC = () => {
  const { t } = useTranslation()
  return (
    <SegmentedChip.Root title={t('warehouses.conditions.locked_hint')}>
      <SegmentedChip.Segment emphasis="muted">
        <LockIcon className="size-3" />
        {t('warehouses.conditions.locked')}
      </SegmentedChip.Segment>
    </SegmentedChip.Root>
  )
}

// Every field, or only the ones a rule context offers; a category left without fields is dropped.
export function useFieldGroups(
  fields = Object.keys(CONDITION_FIELDS) as ConditionField[]
): FieldGroup[] {
  const { t } = useTranslation()
  return CONDITION_CATEGORIES.map((category) => ({
    label: t(`warehouses.conditions.categories.${category}`),
    items: fields
      .filter((field) => fieldSpec(field).category === category)
      .map((field) => ({
        value: field,
        label: t(`warehouses.conditions.fields.${field}`)
      }))
  })).filter((group) => group.items.length > 0)
}
